/**
 * Admin Revenue & Performance Dashboard (Phase 8)
 *
 * Route: /admin/dashboard
 * IST-aware. Pulls from /api/v2/admin/analytics/*.
 *
 * Sections:
 *  1. KPI cards (6 total) — today (IST)
 *  2. Revenue timeline chart — 7d / 30d toggle
 *  3. Event performance table — sortable + paginated
 *  4. Payment breakdown — gateway / wallet / refund / net split
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  TrendingUp, IndianRupee, Ticket, Clock, RotateCcw, Wallet,
  ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight,
  CalendarDays, CreditCard, Sparkles,
} from 'lucide-react';

import AdminShell from '../../components/admin/AdminShell';
import { KpiCard } from '../../components/admin/KpiCard';
import { Button } from '../../components/ui/button';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';

import { adminV2Api } from '../../lib/api';
import { cn, formatCurrency, formatDate } from '../../lib/utils';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const SORT_COLUMNS = [
  { key: 'event_date',          label: 'Event Date' },
  { key: 'tickets_sold',        label: 'Tickets Sold' },
  { key: 'revenue',             label: 'Revenue' },
  { key: 'refund_amount',       label: 'Refunds' },
  { key: 'checkins',            label: 'Check-ins' },
  { key: 'inventory_remaining', label: 'Inv. Remaining %' },
];

const PAGE_SIZE = 10;

function compactCurrency(n) {
  if (n == null || isNaN(n)) return '₹0';
  const abs = Math.abs(n);
  if (abs >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(1)}Cr`;
  if (abs >= 1_00_000)    return `₹${(n / 1_00_000).toFixed(1)}L`;
  if (abs >= 1_000)       return `₹${(n / 1_000).toFixed(1)}K`;
  return `₹${n}`;
}

function shortDateLabel(yyyy_mm_dd) {
  // Display "06 Jun" style labels on the X axis
  if (!yyyy_mm_dd) return '';
  const [y, m, d] = yyyy_mm_dd.split('-').map(Number);
  if (!y || !m || !d) return yyyy_mm_dd;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function StatusBadge({ status }) {
  const colorMap = {
    published: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
    draft:     'bg-zinc-500/15 text-zinc-300 border border-zinc-500/30',
    completed: 'bg-sky-500/15 text-sky-300 border border-sky-500/30',
    archived:  'bg-stone-500/15 text-stone-300 border border-stone-500/30',
  };
  return (
    <Badge className={cn('font-medium', colorMap[status] || colorMap.draft)} data-testid={`event-status-${status}`}>
      {status || '—'}
    </Badge>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────
function KpiSkeleton() {
  return (
    <div className="surface-card card-highlight rounded-xl p-4" data-testid="kpi-skeleton">
      <Skeleton className="h-3 w-20 bg-[#202020] mb-3" />
      <Skeleton className="h-9 w-28 bg-[#202020] mb-2" />
      <Skeleton className="h-3 w-32 bg-[#202020]" />
    </div>
  );
}

function RevenueChart({ data, days, loading }) {
  if (loading) {
    return (
      <div className="surface-card rounded-xl p-6 h-[340px] flex flex-col gap-3" data-testid="revenue-chart-skeleton">
        <Skeleton className="h-4 w-40 bg-[#202020]" />
        <Skeleton className="flex-1 w-full bg-[#202020]" />
      </div>
    );
  }
  const hasData = data?.series?.some((d) => d.gross_revenue > 0 || d.refunds_total > 0);
  return (
    <div className="surface-card rounded-xl p-4 lg:p-6" data-testid="revenue-chart">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <div>
          <p className="text-xs text-[#B9B9B9] uppercase tracking-wider font-medium">
            Revenue · last {days} days (IST)
          </p>
          <p className="font-['EB_Garamond'] text-2xl text-[#F2F2F2] tabular-nums mt-1">
            {formatCurrency(data?.totals?.net_revenue || 0)}
          </p>
          <p className="text-xs text-[#B9B9B9] mt-0.5">
            Gross {formatCurrency(data?.totals?.gross_revenue || 0)}
            {' · '}
            Refunds {formatCurrency(data?.totals?.refunds_total || 0)}
            {' · '}
            {data?.totals?.bookings || 0} bookings
          </p>
        </div>
      </div>
      {hasData ? (
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={data?.series || []} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#202020" />
              <XAxis
                dataKey="date"
                tickFormatter={shortDateLabel}
                stroke="#B9B9B9"
                tick={{ fontSize: 11 }}
              />
              <YAxis
                stroke="#B9B9B9"
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => compactCurrency(v)}
                width={64}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#121212',
                  border: '1px solid #2A2A2A',
                  borderRadius: 8,
                  color: '#F2F2F2',
                  fontSize: 12,
                }}
                formatter={(value, name) => [formatCurrency(value), name]}
                labelFormatter={(label) => shortDateLabel(label)}
              />
              <Legend wrapperStyle={{ fontSize: 12, color: '#B9B9B9' }} />
              <Line
                type="monotone"
                dataKey="gross_revenue"
                name="Gross"
                stroke="#D4A843"
                strokeWidth={2}
                dot={{ r: 3, fill: '#D4A843' }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="net_revenue"
                name="Net"
                stroke="#2FBF71"
                strokeWidth={2}
                dot={{ r: 2.5, fill: '#2FBF71' }}
              />
              <Line
                type="monotone"
                dataKey="refunds_total"
                name="Refunds"
                stroke="#E05252"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={{ r: 2, fill: '#E05252' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-[260px] flex flex-col items-center justify-center text-center" data-testid="revenue-chart-empty">
          <Sparkles className="text-[#D4A843] mb-3" size={28} />
          <p className="text-[#B9B9B9] text-sm">No revenue in the last {days} days yet.</p>
          <p className="text-xs text-[#7A7A7A] mt-1">As bookings get paid, you’ll see them appear here.</p>
        </div>
      )}
    </div>
  );
}

function PaymentBreakdownCard({ data, loading }) {
  if (loading) {
    return (
      <div className="surface-card rounded-xl p-6 space-y-4" data-testid="payment-breakdown-skeleton">
        <Skeleton className="h-4 w-40 bg-[#202020]" />
        <Skeleton className="h-9 w-32 bg-[#202020]" />
        <Skeleton className="h-3 w-full bg-[#202020]" />
        <Skeleton className="h-3 w-full bg-[#202020]" />
        <Skeleton className="h-3 w-full bg-[#202020]" />
      </div>
    );
  }
  const gross   = data?.gross_revenue || 0;
  const gateway = data?.gateway_revenue || 0;
  const wallet  = data?.wallet_revenue || 0;
  const refunds = data?.refunds_total || 0;
  const net     = data?.net_collected || 0;
  const pct = (n) => (gross > 0 ? Math.round((n / gross) * 100) : 0);

  const Row = ({ label, value, color, percent, testId }) => (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-[#B9B9B9]">{label}</span>
        <span className="text-[#F2F2F2] tabular-nums">
          {formatCurrency(value)}{percent != null && <span className="text-[#7A7A7A] text-xs ml-1">({percent}%)</span>}
        </span>
      </div>
      <div className="h-1.5 bg-[#1A1A1A] rounded overflow-hidden">
        <div
          className="h-full rounded transition-all"
          style={{ width: `${Math.min(100, percent ?? 0)}%`, background: color }}
        />
      </div>
    </div>
  );

  return (
    <div className="surface-card rounded-xl p-6" data-testid="payment-breakdown">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-xs text-[#B9B9B9] uppercase tracking-wider font-medium">Payment Breakdown</p>
          <p className="font-['EB_Garamond'] text-2xl text-[#F2F2F2] tabular-nums mt-1" data-testid="payment-breakdown-net">
            {formatCurrency(net)}
          </p>
          <p className="text-xs text-[#B9B9B9] mt-0.5">
            Net collected · {data?.bookings || 0} bookings
          </p>
        </div>
        <div className="w-10 h-10 rounded-lg bg-[#D4A843]/10 flex items-center justify-center">
          <CreditCard size={18} className="text-[#D4A843]" />
        </div>
      </div>
      <div className="space-y-4">
        <Row label="Gateway Revenue" value={gateway} color="#D4A843" percent={pct(gateway)} testId="pb-gateway" />
        <Row label="Wallet Revenue"  value={wallet}  color="#7BB7E8" percent={pct(wallet)}  testId="pb-wallet" />
        <Row label="Refunds"         value={refunds} color="#E05252" percent={pct(refunds)} testId="pb-refunds" />
      </div>
    </div>
  );
}

function EventPerformanceTable({ rows, total, loading, sortBy, sortDir, onSort, page, onPage }) {
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const SortIcon = ({ active, dir }) => {
    if (!active) return <ArrowUpDown size={12} className="opacity-50 ml-1 inline" />;
    return dir === 'desc'
      ? <ArrowDown size={12} className="text-[#D4A843] ml-1 inline" />
      : <ArrowUp   size={12} className="text-[#D4A843] ml-1 inline" />;
  };

  return (
    <div className="surface-card rounded-xl overflow-hidden" data-testid="event-performance">
      <div className="p-4 lg:p-6 border-b border-[#202020]">
        <p className="text-xs text-[#B9B9B9] uppercase tracking-wider font-medium">Event Performance</p>
        <p className="text-sm text-[#7A7A7A] mt-1">Sortable. Click any column header to re-rank.</p>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-[#202020]">
              <TableHead className="text-[#B9B9B9]">Event</TableHead>
              {SORT_COLUMNS.map((c) => (
                <TableHead
                  key={c.key}
                  onClick={() => onSort(c.key)}
                  className="cursor-pointer select-none text-[#B9B9B9] hover:text-[#F2F2F2] text-right whitespace-nowrap"
                  data-testid={`sort-${c.key}`}
                >
                  <span className="inline-flex items-center">
                    {c.label}
                    <SortIcon active={sortBy === c.key} dir={sortDir} />
                  </span>
                </TableHead>
              ))}
              <TableHead className="text-[#B9B9B9] text-right">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <>
                {[...Array(5)].map((_, i) => (
                  <TableRow key={i} className="border-[#202020]">
                    <TableCell colSpan={8}>
                      <Skeleton className="h-5 w-full bg-[#202020]" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {!loading && rows?.length === 0 && (
              <TableRow className="border-[#202020]" data-testid="event-performance-empty">
                <TableCell colSpan={8} className="text-center py-10">
                  <p className="text-[#B9B9B9] text-sm">No events yet.</p>
                  <p className="text-xs text-[#7A7A7A] mt-1">
                    Once you publish events and they start receiving bookings, you’ll see them here.
                  </p>
                </TableCell>
              </TableRow>
            )}
            {!loading && rows?.map((r) => (
              <TableRow
                key={r.event_id}
                className="border-[#202020] hover:bg-[#161616]"
                data-testid={`event-row-${r.event_id}`}
              >
                <TableCell className="text-[#F2F2F2]">
                  <div className="flex flex-col">
                    <span className="font-medium">{r.event_title}</span>
                    <span className="text-xs text-[#7A7A7A]">
                      {r.event_slug || r.event_id?.slice(0, 8)}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-right text-[#B9B9B9] whitespace-nowrap">
                  {formatDate(r.event_date)}{r.event_time ? ` · ${r.event_time}` : ''}
                </TableCell>
                <TableCell className="text-right text-[#F2F2F2] tabular-nums">{r.tickets_sold}</TableCell>
                <TableCell className="text-right text-[#F2F2F2] tabular-nums">{formatCurrency(r.gross_revenue)}</TableCell>
                <TableCell className="text-right tabular-nums" style={{ color: r.refunds_total ? '#E05252' : '#7A7A7A' }}>
                  {formatCurrency(r.refunds_total)}
                </TableCell>
                <TableCell className="text-right text-[#F2F2F2] tabular-nums">
                  {r.checkins_count}
                  {r.tickets_sold > 0 && (
                    <span className="text-xs text-[#7A7A7A] ml-1">
                      ({Math.round((r.checkins_count / r.tickets_sold) * 100)}%)
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-1.5 bg-[#1A1A1A] rounded overflow-hidden hidden lg:block">
                      <div
                        className="h-full bg-[#D4A843]"
                        style={{ width: `${Math.min(100, r.inventory_remaining_pct ?? 0)}%` }}
                      />
                    </div>
                    <span className="text-[#F2F2F2]">{r.inventory_remaining_pct ?? 0}%</span>
                  </div>
                </TableCell>
                <TableCell className="text-right"><StatusBadge status={r.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {!loading && total > PAGE_SIZE && (
        <div className="flex items-center justify-between p-4 border-t border-[#202020]" data-testid="event-performance-pagination">
          <p className="text-xs text-[#B9B9B9]">
            Page {page + 1} of {totalPages} · {total} events
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPage(Math.max(0, page - 1))}
              disabled={page === 0}
              data-testid="event-perf-prev"
            >
              <ChevronLeft size={14} className="mr-1" />Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              data-testid="event-perf-next"
            >
              Next<ChevronRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
export default function AnalyticsDashboardPage() {
  const navigate = useNavigate();

  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(true);

  const [timelineDays, setTimelineDays] = useState(7);
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(true);

  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(true);

  const [sortBy, setSortBy]   = useState('event_date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage]       = useState(0);
  const [events, setEvents]   = useState(null);
  const [eventsLoading, setEventsLoading] = useState(true);

  // 1) KPIs
  useEffect(() => {
    setKpisLoading(true);
    adminV2Api.analyticsKpis()
      .then((r) => setKpis(r.data))
      .catch((err) => {
        console.error('KPI load failed', err);
        toast.error('Failed to load KPIs');
      })
      .finally(() => setKpisLoading(false));
  }, []);

  // 2) Timeline (re-fetches when toggle changes)
  useEffect(() => {
    setTimelineLoading(true);
    adminV2Api.analyticsRevenueTimeline(timelineDays)
      .then((r) => setTimeline(r.data))
      .catch((err) => {
        console.error('timeline failed', err);
        toast.error('Failed to load timeline');
      })
      .finally(() => setTimelineLoading(false));
  }, [timelineDays]);

  // 3) Payment Breakdown (all-time by default)
  useEffect(() => {
    setBreakdownLoading(true);
    adminV2Api.analyticsPaymentBreakdown(null)
      .then((r) => setBreakdown(r.data))
      .catch((err) => {
        console.error('breakdown failed', err);
        toast.error('Failed to load payment breakdown');
      })
      .finally(() => setBreakdownLoading(false));
  }, []);

  // 4) Event Performance
  useEffect(() => {
    setEventsLoading(true);
    adminV2Api.analyticsEventPerformance({
      sort_by:  sortBy,
      sort_dir: sortDir,
      skip:     page * PAGE_SIZE,
      limit:    PAGE_SIZE,
    })
      .then((r) => setEvents(r.data))
      .catch((err) => {
        console.error('event-performance failed', err);
        toast.error('Failed to load event performance');
      })
      .finally(() => setEventsLoading(false));
  }, [sortBy, sortDir, page]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
    setPage(0);
  };

  return (
    <AdminShell title="Dashboard">
      <div className="space-y-6" data-testid="analytics-dashboard">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] leading-tight">
              Revenue & Performance
            </h1>
            <p className="text-sm text-[#B9B9B9] mt-1">
              All figures IST · refreshes when you reload
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/operations')}
            data-testid="goto-operations"
          >
            Operations View
          </Button>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="kpi-grid">
          {kpisLoading ? (
            <>{[...Array(6)].map((_, i) => <KpiSkeleton key={i} />)}</>
          ) : (
            <>
              <KpiCard
                title="Gross Revenue (Today)"
                value={formatCurrency(kpis?.gross_revenue || 0)}
                subtitle={`${kpis?.bookings_count || 0} bookings`}
                icon={IndianRupee}
                data-testid="kpi-gross"
              />
              <KpiCard
                title="Net Revenue (Today)"
                value={formatCurrency(kpis?.net_revenue || 0)}
                subtitle={`After ${formatCurrency(kpis?.refunds_total || 0)} refunds`}
                icon={TrendingUp}
                data-testid="kpi-net"
              />
              <KpiCard
                title="Bookings (Today)"
                value={kpis?.bookings_count ?? 0}
                subtitle="Paid bookings IST"
                icon={Ticket}
                data-testid="kpi-bookings"
              />
              <KpiCard
                title="Pending Approvals"
                value={kpis?.pending_approvals ?? 0}
                subtitle="VIP awaiting decision"
                icon={Clock}
                onClick={() => navigate('/admin/bookings?status=awaiting_approval')}
                className="cursor-pointer"
                data-testid="kpi-pending"
              />
              <KpiCard
                title="Refunds (Today)"
                value={formatCurrency(kpis?.refunds_total || 0)}
                subtitle={`${kpis?.refunds_count || 0} refund events`}
                icon={RotateCcw}
                data-testid="kpi-refunds"
              />
              <KpiCard
                title="Wallet Usage (Today)"
                value={formatCurrency(kpis?.wallet_usage || 0)}
                subtitle={`Gateway ${formatCurrency(kpis?.gateway_revenue || 0)}`}
                icon={Wallet}
                data-testid="kpi-wallet"
              />
            </>
          )}
        </div>

        {/* Timeline + Payment Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-xs text-[#B9B9B9] uppercase tracking-wider font-medium flex items-center gap-2">
                <CalendarDays size={14} className="text-[#D4A843]" />
                Revenue Over Time
              </p>
              <Tabs value={String(timelineDays)} onValueChange={(v) => setTimelineDays(Number(v))}>
                <TabsList className="bg-[#1A1A1A] border border-[#202020]" data-testid="timeline-toggle">
                  <TabsTrigger value="7"  data-testid="timeline-7d">7d</TabsTrigger>
                  <TabsTrigger value="30" data-testid="timeline-30d">30d</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <RevenueChart data={timeline} days={timelineDays} loading={timelineLoading} />
          </div>
          <div>
            <p className="text-xs text-[#B9B9B9] uppercase tracking-wider font-medium flex items-center gap-2 mb-3">
              <CreditCard size={14} className="text-[#D4A843]" />
              Payments
            </p>
            <PaymentBreakdownCard data={breakdown} loading={breakdownLoading} />
          </div>
        </div>

        {/* Event Performance */}
        <EventPerformanceTable
          rows={events?.items}
          total={events?.total ?? 0}
          loading={eventsLoading}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          page={page}
          onPage={setPage}
        />
      </div>
    </AdminShell>
  );
}
